import { Box, Center, Flex, Image, Spinner, useDisclosure } from '@chakra-ui/react'
import { Button, Text, Column, Row } from '@ubirider/pick-component-library'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'motion/react'
import React, { useState } from 'react'

import { Ticket } from './components/Ticket'
import { useCards, useTicketing } from '../../context/DataProvider'
import EmptyStateIllustration from '../../assets/bill_empty.svg'
import PlusIcon from '../../assets/plus_white.svg'
import { NoCardsModal } from '../../modals/NoCardsModal'
import { NewTicketModal } from './components/NewTicketModal/NewTicketModal'

const MFlex = motion.create(Flex)
const MBox = motion.create(Box)
const MCenter = motion.create(Center)
const MColumn = motion.create(Column)
const MRow = motion.create(Row)
const MText = motion.create(Text)

const variants = {
  container: {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
        delayChildren: 0.1
      }
    }
  },
  section: {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { type: 'spring', stiffness: 300, damping: 25 }
    }
  },
  card: {
    hidden: { opacity: 0, y: 15, scale: 0.98 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: { type: 'spring', stiffness: 350, damping: 25 }
    },
    exit: {
      opacity: 0,
      scale: 0.96,
      transition: { duration: 0.2 }
    }
  }
}

export const Tickets = () => {
  const { t } = useTranslation()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const { isOpen: isOpenNoCards, onOpen: onOpenNoCards, onClose: onCloseNoCards } = useDisclosure()

  const { cards } = useCards()
  const { activeTickets, expiredTickets, loading } = useTicketing()

  const [showAllActive, setShowAllActive] = useState(false)
  const [showAllExpired, setShowAllExpired] = useState(false)

  const handleNewTicket = () => {
    if (cards?.length > 0) onOpen()
    else onOpenNoCards()
  }

  const renderTicketList = (tickets, showAll, setShowAll, label) => (
    <MColumn key={label} css={{ gap: '24px' }} variants={variants.container} initial="hidden" animate="visible">
      <MRow variants={variants.section} css={{ alignItems: 'center', justifyContent: 'space-between' }}>
        <MText css={{ fontSize: 16, fontWeight: 900, color: '#90A1B9', textTransform: 'uppercase' }}>
          {label}
        </MText>
        {tickets.length > 2 && (
          <MFlex justify='flex-end'>
            <Button
              onClick={() => setShowAll(!showAll)}
              style='ghost'
              type='secondary'
              size='small'
              label={showAll ? 'ver menos' : 'ver tudo'}
            />
          </MFlex>
        )}
      </MRow>

      <AnimatePresence mode='popLayout'>
        {tickets.slice(0, showAll ? tickets.length : 2).map((ticket) => (
          <MBox key={ticket?.id} variants={variants.card} layout>
            <Ticket ticket={ticket} />
          </MBox>
        ))}
      </AnimatePresence>
    </MColumn>
  )

  return (
    <MFlex
      direction='column'
      gap='20px'
      flex='1'
      initial='hidden'
      animate='visible'
      variants={variants.container}
    >
      <MBox
        flex={1}
        bg={{ base: 'transparent', md: 'neutral.100' }}
        rounded='8px'
        padding={{ md: '24px' }}
        variants={variants.section}
      >
        <MColumn css={{ gap: '32px' }}>
          <MFlex direction={{ base: 'column', lg: 'row' }} justify='space-between' align={{ lg: 'center' }} gap='24px'>
            <MColumn css={{ gap: '12px' }}>
              <MText type='displayXS' weight='bold' variants={variants.section}>
                {t('acqua_vias.your_tickets_title')}
              </MText>
              <MText type='textSM' variants={variants.section}>
                {t('acqua_vias.your_tickets_description')}
              </MText>
            </MColumn>
            <MBox variants={variants.section}>
              <Button
                onClick={handleNewTicket}
                style='solid'
                type='secondary'
                leftIconProps={{ src: PlusIcon }}
                label={t('acqua_vias.your_tickets_new_ticket_button')}
              />
            </MBox>
          </MFlex>

          <AnimatePresence mode='wait'>
            {loading ? (
              <MCenter key='loading' minH='300px' variants={variants.section} exit={{ opacity: 0 }}>
                <Spinner thickness='6px' color='neutral.500' size='xl' />
              </MCenter>
            ) : activeTickets.length > 0 || expiredTickets.length > 0 ? (
              <MColumn key='content' css={{ gap: '40px' }} variants={variants.container}>
                {activeTickets.length > 0 &&
                  renderTicketList(activeTickets, showAllActive, setShowAllActive, 'Bilhetes disponíveis')
                }
                {expiredTickets.length > 0 &&
                  renderTicketList(expiredTickets, showAllExpired, setShowAllExpired, t('acqua_vias.your_tickets_ticket_history'))
                }
              </MColumn>
            ) : (
              <MFlex
                key='empty'
                direction='column'
                align='center'
                justify='center'
                py='60px'
                gap='24px'
                variants={variants.section}
              >
                <Image src={EmptyStateIllustration} height='160px' />
                <MText type='textSM' css={{ textAlign: 'center' }}>
                  {t('acqua_vias.your_ticket_no_tickets_available_empty_state')}
                </MText>
              </MFlex>
            )}
          </AnimatePresence>
        </MColumn>
      </MBox>

      <NoCardsModal isOpen={isOpenNoCards} onClose={onCloseNoCards} />
      <NewTicketModal isOpen={isOpen} onClose={onClose} />
    </MFlex>
  )
}